import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Mail, Clock3, CheckCircle2, XCircle } from "lucide-react";
import { api, apiError } from "../lib/api";
import { fmtDateStr } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const STATUS = {
  pending: { label: "Pending", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", Icon: Clock3 },
  approved: { label: "Approved", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", Icon: CheckCircle2 },
  declined: { label: "Declined", cls: "bg-rose-500/15 text-rose-300 border-rose-500/30", Icon: XCircle },
};

const normApprovers = (list) => (list || []).map((a) => (typeof a === "string" ? { email: a, required: false } : { email: a.email || "", required: !!a.required }));

export function ApprovalControls({ type, from, to, company }) {
  const [open, setOpen] = useState(false);
  const [auto, setAuto] = useState(true);
  const [sending, setSending] = useState(false);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState({}); // email -> bool (optional approvers only)
  const [extra, setExtra] = useState("");

  const approvers = useMemo(() => normApprovers(company?.approvers), [company]);
  const required = useMemo(() => approvers.filter((a) => a.required), [approvers]);
  const optional = useMemo(() => approvers.filter((a) => !a.required), [approvers]);
  const autoLabel = type === "schedule"
    ? "Auto-publish & notify staff once all required approvers approve"
    : "Auto-send payroll to the processor once all required approvers approve";

  const load = useCallback(() => {
    api.get(`/approvals/requests?type=${type}`).then(({ data }) => setRequests(data.requests || [])).catch(() => {});
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const openModal = () => { setSelected({}); setExtra(""); setAuto(true); setOpen(true); };

  const send = async () => {
    const targets = [
      ...required.map((a) => ({ email: a.email, required: true })),
      ...optional.filter((a) => selected[a.email]).map((a) => ({ email: a.email, required: false })),
    ];
    extra.split(/[\n,]/).map((s) => s.trim()).filter(Boolean).forEach((e) => {
      if (!targets.some((t) => t.email.toLowerCase() === e.toLowerCase())) targets.push({ email: e, required: false });
    });
    if (targets.length === 0) { toast.error("Select or enter at least one approver"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/approvals/request", {
        type, approvers: targets, auto_action: auto,
        base_url: window.location.origin, from_date: from, to_date: to,
      });
      toast.success(data.emailed ? `Approval request emailed to ${data.count} approver${data.count === 1 ? "" : "s"}` : "Request created (email not configured)");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Failed to send"); }
    finally { setSending(false); }
  };

  const recent = requests.slice(0, 4);
  return (
    <>
      <button data-testid={`send-approval-${type}-button`} onClick={openModal}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 font-semibold bg-slate-800 hover:bg-slate-700 transition-colors">
        <Mail className="w-4 h-4" /> Send for approval
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-[90vh] overflow-y-auto" data-testid={`approval-modal-${type}`}>
          <DialogHeader><DialogTitle>Send {type} for approval</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-sm text-slate-400">Each approver receives an email with Approve / Decline buttons and can review the full {type} for {fmtDateStr(from)} – {fmtDateStr(to)} on a secure page.</p>

            {required.length > 0 && (
              <div data-testid="required-approvers-block">
                <div className="text-xs font-semibold uppercase tracking-wider text-blue-300 mb-1.5">Required sign-offs ({required.length})</div>
                <div className="space-y-1.5">
                  {required.map((a) => (
                    <div key={a.email} data-testid={`required-approver-${a.email.replace(/[^a-z0-9]/gi, "-")}`} className="flex items-center gap-2 text-sm rounded-lg bg-blue-500/10 border border-blue-500/25 px-3 py-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-300 shrink-0" />
                      <span className="truncate">{a.email}</span>
                      <span className="ml-auto text-[10px] font-semibold text-blue-300">Required</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">The {type} won't auto-publish until every required approver signs off.</p>
              </div>
            )}

            {optional.length > 0 && (
              <div data-testid="optional-approvers-block">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Optional reviewers</div>
                <div className="space-y-1.5">
                  {optional.map((a) => (
                    <label key={a.email} className="flex items-center gap-2 text-sm rounded-lg bg-slate-950 border border-slate-700 px-3 py-2 cursor-pointer">
                      <input data-testid={`optional-approver-${a.email.replace(/[^a-z0-9]/gi, "-")}`} type="checkbox" checked={!!selected[a.email]}
                        onChange={(e) => setSelected((s) => ({ ...s, [a.email]: e.target.checked }))} className="w-4 h-4 accent-blue-600" />
                      <span className="truncate">{a.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-300">Additional approver email (optional)</label>
              <input data-testid="approver-email-input" type="email" value={extra} onChange={(e) => setExtra(e.target.value)}
                placeholder="someone@company.com" className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none" />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input data-testid="approval-auto-toggle" type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} className="w-4 h-4 accent-blue-600" />
              {autoLabel}
            </label>
            <button data-testid="approval-send-button" onClick={send} disabled={sending}
              className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-60 py-2.5 font-semibold transition-colors">
              {sending ? "Sending…" : "Send request"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {recent.length > 0 && (
        <div className="basis-full w-full" data-testid={`approval-requests-${type}`}>
          <div className="flex flex-wrap gap-2 mt-1">
            {recent.map((r) => {
              const S = STATUS[r.status] || STATUS.pending;
              return (
                <span key={r.id} data-testid={`approval-request-${r.id}`} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${S.cls}`} title={`${r.approver_email} · ${fmtDateStr(r.period_start)} to ${fmtDateStr(r.period_end)}${r.batch_id ? (r.required ? " · required" : " · optional") : ""}`}>
                  <S.Icon className="w-3.5 h-3.5" /> {fmtDateStr(r.period_start)} → {fmtDateStr(r.period_end)}: {S.label}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
